// Segmentation · trait normalisation and the published dimension table.
//
// Every raw value the extractor emits is messy — "moderate to high", "$3,200",
// "family-with-children" — so each dimension folds its long tail into a small
// fixed bucket set before anything counts it. Those foldings are published
// numbers: the Explorer's behaviour wall draws bars from them, and the
// assistant answers questions about the same cohort from them.
//
// They live here, in @ow/shared, for the reason rankings.ts and
// destination-mentions.ts do: when the two sides bucket differently they
// disagree in public about the same profiles. The Explorer's "Where they go"
// map had exactly that failure in a milder form — the assistant simply had no
// access — and the fix is the same one, applied before the divergence exists.
//
// Moved verbatim from packages/web/.../segmentation-ui/engine.ts, which now
// re-exports every name below so the web keeps its existing call sites.

const INCOME_BUCKETS = ["low", "mid", "upper-mid", "high"];
/** Fold a raw extracted income into one of the four buckets ("mid-high" counts
 *  as "high"). Anything we can't place — missing, or an off-bucket label
 *  ("middle", "affluent", a raw dollar figure) — folds into "unknown" so the
 *  distribution always sums to 100 (no silent dropouts). */
export function normalizeIncome(v: string | null): string {
  if (!v) return "unknown";
  const t = v.trim();
  const s = t === "mid-high" ? "high" : t;
  return INCOME_BUCKETS.includes(s) ? s : "unknown";
}
const AGE_BUCKETS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
/** Place a raw extracted age into one of the six buckets. A value already in a
 *  bucket passes through; a bare number is binned; anything we can't place —
 *  missing, or a string with no usable number ("20s", "millennial") — folds
 *  into "unknown" so the distribution always sums to 100 (no silent dropouts). */
export function normalizeAge(v: string | null): string {
  if (!v) return "unknown";
  const s = v.trim();
  if (AGE_BUCKETS.includes(s)) return s;
  const m = s.match(/\d{1,3}/);
  if (m) {
    const n = Number.parseInt(m[0], 10);
    if (n >= 18 && n <= 24) return "18-24";
    if (n >= 25 && n <= 34) return "25-34";
    if (n >= 35 && n <= 44) return "35-44";
    if (n >= 45 && n <= 54) return "45-54";
    if (n >= 55 && n <= 64) return "55-64";
    if (n >= 65) return "65+";
  }
  return "unknown";
}
/** Household status arrives in both spellings of everything — "family with
 *  children", "family_with_children", "Married Couple" — so the separator and
 *  the case are flattened before the value is counted. Without this the same
 *  household reads as three different buckets. */
export function normalizeHousehold(v: string | null): string | null {
  const s = meaningfulFrag(v);
  return s ? s.toLowerCase().replace(/[\s-]+/g, "_") : null;
}
export function normalizeFrequency(v: string | null): string | null {
  if (!v) return null;
  if (v.startsWith("very_frequent")) return "very_frequent";
  if (v.startsWith("frequent")) return "frequent";
  if (v.startsWith("regular")) return "frequent";
  if (v.startsWith("occasional")) return "occasional";
  // trip_frequency_per_year is extracted as a NUMBER of trips per year now, not
  // as one of the three bucket words — so a bare "1"/"4" used to pass straight
  // through, land outside `order`, and get dropped, blanking the card for every
  // profile that actually had a frequency. Bin the number instead.
  const n = Number.parseFloat(v.match(/\d+(\.\d+)?/)?.[0] ?? "");
  if (Number.isFinite(n) && n > 0) {
    if (n >= 6) return "very_frequent";
    if (n >= 3) return "frequent";
    return "occasional";
  }
  return v;
}
export function normalizeSpend(v: string | null): string | null {
  if (!v) return null;
  if (v === "<500" || v === "500-2K" || v === "2K-5K" || v === "5K+") return v;
  if (v === "2-5K") return "2K-5K";
  if (v === "10-25K") return "5K+";
  if (v.startsWith("5K") || /^\d+K\+$/.test(v)) return "5K+";
  // spend_per_trip_usd is now extracted as a raw USD total ("5000", "$3,200",
  // "2000-3000 USD") — bucket the first number into the per-trip bands. "K"
  // suffixes ("3K", "1.5k") count as thousands.
  const m = v.replace(/,/g, "").match(/(\d+(?:\.\d+)?)\s*([kK])?/);
  if (m) {
    const n = Number.parseFloat(m[1]!) * (m[2] ? 1000 : 1);
    if (Number.isFinite(n) && n > 0) {
      if (n < 500) return "<500";
      if (n < 2000) return "500-2K";
      if (n < 5000) return "2K-5K";
      return "5K+";
    }
  }
  return v;
}
export function normalizeCompanion(v: string | null): string | null {
  if (!v) return null;
  if (v === "family-with-kids" || v === "family_with_kids") return "family_with_kids";
  return v;
}
export function normalizeAccom(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  // Free-text accommodation tier from the LLM → fold the long tail into the five
  // display buckets. Order matters (more specific first). Anything unmappable
  // (bare "hotel", "apartment", "airbnb") falls through to the caller's unknown
  // bucket rather than rendering as its own one-off slice. Hostel/camping folds
  // into "budget" — they're the same value tier for this audience.
  if (/lux|5[\s_-]?star|five[\s_-]?star|ultra|palace|opulent|high[\s_-]?end|premium\s*resort/.test(s)) return "luxury";
  if (/boutique|design\s*hotel|lifestyle\s*hotel/.test(s)) return "boutique";
  if (/upscale|upper[\s_-]?(?:up)?scale|4[\s_-]?star|four[\s_-]?star|premium|business\s*hotel/.test(s))
    return "upscale_chain";
  if (/midscale|mid[\s_-]?scale|mid[\s_-]?range|moderate|3[\s_-]?star|three[\s_-]?star|standard|chain/.test(s))
    return "midscale_chain";
  if (
    /budget|economy|cheap|value|affordable|2[\s_-]?star|1[\s_-]?star|motel|guest[\s_-]?house|inn\b|hostel|camp|tent|backpack|caravan|\brv\b|couch|dorm/.test(
      s,
    )
  )
    return "budget";
  return v;
}
export function normalizeScope(v: string | null): string | null {
  if (!v) return null;
  // Two buckets only: domestic vs international. "Regional" (same-continent,
  // cross-border) and "intercontinental" both fold into international —
  // anything that crosses a national border is international travel.
  if (v === "domestic") return "domestic";
  return "international";
}
/** Fold a free-text barrier fragment into one of four broad categories. The
 *  raw general_barriers field is almost always a unique long phrase ("Harassment
 *  risks in India for women travelers"), so bucketing is the only way to make it
 *  countable. Split the raw value on delimiters first, then map each fragment.
 *  Order matters (safety wins over a co-mentioned cost/visa). Default: personal. */
export function normalizeBarriers(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (
    /safe|harass|assault|crime|theft|steal|scam|danger|unwelcom|bias|profil|racis|racial|discrim|conflict|\bwar\b|geopolit|politic|unrest|terror|violence/.test(
      s,
    )
  )
    return "safety & security";
  if (
    /visa|passport|customs|immigrat|border|\bentry\b|schengen|flight|airport|airline|delay|cancel|connection|layover|logistic|infrastructur|weather/.test(
      s,
    )
  )
    return "entry & logistics";
  if (
    /cost|price|expensive|afford|budget|money|\bfee|fare|deval|points|miles|award|redempt|blackout|refund|upgrade/.test(
      s,
    )
  )
    return "cost";
  return "personal";
}
/** Fold a free-text trigger_moments fragment into one of four broad categories.
 *  Like barriers, the raw value is almost always a unique phrase ("Milestone
 *  birthdays trigger gatherings"), so bucketing is the only way to make it
 *  countable. Split the raw value first, then map each fragment. Order matters.
 *  Default: seasonal & social (the residual home for holiday/faith/event/content
 *  moments that aren't a milestone, a deal, or a life change). */
export function normalizeTriggers(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/birthday|anniversar|wedding|honeymoon|engage|propos|graduat|milestone|\bbaby\b|newborn|retire/.test(s))
    return "milestone & celebration";
  if (
    /deal|fare|bonus|\bpoints?\b|\bmiles?\b|award|deval|upgrade|redempt|program|mistake|cashback|discount|\bsale\b/.test(
      s,
    )
  )
    return "deals & rewards";
  if (
    /solo|break[\s_-]?up|divorce|career|sabbatical|\bmove\b|reloc|\bquit\b|\bjob\b|transition|life[\s_-]?(change|quit)|burnout|escape|clarity/.test(
      s,
    )
  )
    return "life transition";
  return "seasonal & social";
}
/** Fold a raw budget-sensitivity value into the six display buckets. The LLM
 *  usually emits the bucket ids verbatim ("experience_over_price", "price_aware")
 *  but can phrase them freely, so match on the lowercased value with a sensible
 *  default; anything unplaceable falls through to the caller's unknown bucket. */
export function normalizeSensitivity(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase().replace(/[\s-]+/g, "_");
  // Order matters: the more specific / stronger signals are tested first so a
  // phrase that mentions "experience" beats a bare "premium", etc.
  if (
    /very_price_sensitive|very_price|highly_price|price_sensitive|budget_first|budget_travel|tight_budget|low_budget|shoestring|frugal|thrifty|cheap|penny|bargain|economical|money_tight|can'?t_afford/.test(
      s,
    )
  )
    return "very_price_sensitive";
  if (
    /experience_over_price|experience_first|experience_driven|splurge_selective|willing_to_pay|quality_over_price|quality_first|quality_driven|invest_in_experience|pay_for_quality|splurge_on_experience|worth_the_(money|price|splurge)/.test(
      s,
    )
  )
    return "experience_over_price";
  if (
    /price_insensitive|money_no_object|cost_no_object|price_is_no|price_no_object|spares?_no_expense|regardless_of_(cost|price)|luxury|high_end|ultra_luxury|affluent|big_spender|high_spender|deep_pocket|unconstrained/.test(
      s,
    )
  )
    return "price_insensitive";
  if (
    /price_aware|value_conscious|cost_conscious|price_conscious|budget_conscious|budget_aware|cost_aware|value_for_money|value_seeking|value_oriented|value_driven|smart_spender|deal|discount|coupon/.test(
      s,
    )
  )
    return "price_aware";
  if (
    /aspirational|stretch|trade_up|save_up|aspires?|treat_(themselves|myself)|occasional_splurge|splurge_occasionally/.test(
      s,
    )
  )
    return "aspirational";
  if (
    /mixed|balanced|moderate|selective|flexible|situational|depends|context|mid_range|midrange|middle|average|varies/.test(
      s,
    )
  )
    return "mixed";
  return v;
}

// The remaining behavioural columns are free-text from the LLM extractor, so the
// raw values fan out into dozens of near-identical phrasings ("moderate",
// "moderate to high", "moderate-high", …). Each normaliser folds that long tail
// into a small fixed bucket set (3–4) on a regex over the lowercased value, with
// a sensible default so nothing leaks through as its own slice.
export function normalizeResearch(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/very[\s_-]?high|extensive|exhaustive|obsessive|deep|thorough/.test(s)) return "high";
  if (/high/.test(s)) return "high";
  if (/moderate|medium|\bmid\b|average/.test(s)) return "moderate";
  if (/low|light|minimal|none|spontaneous|opportunistic|impulse/.test(s)) return "low";
  return "moderate";
}
export function normalizeDuration(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/extend|long[\s_-]?stay|month|3\+?\s*week|few\s*week/.test(s)) return "extended";
  if (/two[\s_-]?week|2\s*week|fortnight|10[\s-]*14|14\s*day/.test(s)) return "two_weeks";
  if (/weekend|short|few\s*day|2[\s-]*3\s*day|long[\s_-]?weekend|getaway|3\s*day/.test(s)) return "short_break";
  return "one_week";
}
export function normalizeTransport(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/flight|fly|air|plane|aviation/.test(s)) return "flight";
  if (/car|road|drive|rv|self[\s_-]?drive|motor|van/.test(s)) return "car";
  if (/train|rail/.test(s)) return "train";
  return "other";
}
export function normalizeBooking(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/agent|tour\s*operator|agency/.test(s)) return "agent";
  if (/direct|airline|hotel\s*(web|site|direct)|brand/.test(s)) return "direct";
  return "ota"; // ota / booking.com / expedia / airbnb / app / online platforms
}
export function normalizeLeadTime(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/year|12\s*month|always[\s_-]?plan|far\s*in\s*advance/.test(s)) return "year_plus";
  if (/month/.test(s)) return "months";
  if (/last[\s_-]?minute|spontaneous|impulse|few\s*day|days\b/.test(s)) return "last_minute";
  return "weeks";
}
export function normalizeSeason(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/winter|ski|snow/.test(s)) return "winter";
  if (/summer/.test(s)) return "summer";
  if (/shoulder|spring|fall|autumn|off[\s_-]?peak/.test(s)) return "shoulder";
  return "year_round"; // year-round / anytime / flexible / opportunistic / event-driven
}

/* ── Open-vocabulary traits ───────────────────────────────────────────────
   The rest of the Demand Sensing columns are list-valued free text: a profile
   carries several brands / channels / barriers in one ";"-joined string. They
   were extracted and exported to the workbook but never charted here, so the
   Explorer showed 12 of the sheet's 24 behavioural columns. They are charted
   the same way as everything else — split into fragments, folded into a bucket
   set where one exists, ranked by frequency where it doesn't. */

/** "unknown" survives inside a JOINED value ("experiences; unknown") even though
 *  the server drops a whole-value "unknown", so every fragment is re-checked. */
const MEANINGLESS_FRAGMENTS = new Set([
  "unknown",
  "none",
  "n/a",
  "na",
  "null",
  "undetermined",
  "unspecified",
  "not specified",
  "not mentioned",
]);
export function meaningfulFrag(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  return s && !MEANINGLESS_FRAGMENTS.has(s.toLowerCase()) ? s : null;
}

/** Title-ish casing for an open-vocabulary proper noun ("tripadvisor" and
 *  "TripAdvisor" are the same brand and must not be two bars). Values that
 *  already carry internal capitals keep theirs. */
export function canonicalName(v: string | null): string | null {
  const s = meaningfulFrag(v);
  if (!s) return null;
  if (/[A-Z]/.test(s.slice(1))) return s; // already cased (TripAdvisor, DJI, At The Top)
  return s.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** A verbatim keyword/phrase fragment, made countable. Lowercased (these are
 *  phrases and hashtags, not proper nouns — title-casing them yields "World'S"),
 *  and anything that can't be a keyword on its own (a bare number, a one-word
 *  stub, a sentence) is dropped rather than charted as a bucket of one. */
export function normalizeKeyword(v: string | null): string | null {
  const s = meaningfulFrag(v);
  if (!s) return null;
  const k = s.toLowerCase().replace(/\s+/g, " ").trim();
  if (k.length < 4 || k.length > 40) return null;
  if (!/[a-z]/.test(k)) return null; // bare numbers ("10", "148")
  return k;
}

/** A free-text label used as its own bucket (profession, household) — case and
 *  separator flattened so one value isn't counted as three. */
export function normalizeLabel(v: string | null): string | null {
  const s = meaningfulFrag(v);
  return s
    ? s
        .toLowerCase()
        .replace(/\s*\/\s*/g, " / ")
        .replace(/\s+/g, " ")
    : null;
}

/** Observed trip count → four countable bands. */
export function normalizeTripsObserved(v: string | null): string | null {
  const n = Number.parseFloat((v ?? "").match(/\d+(\.\d+)?/)?.[0] ?? "");
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 2) return "1";
  if (n <= 3) return "2-3";
  if (n <= 6) return "4-6";
  return "7+";
}

/** How much history the read spans, in months → four bands. */
export function normalizeObsWindow(v: string | null): string | null {
  const n = Number.parseFloat((v ?? "").match(/\d+(\.\d+)?/)?.[0] ?? "");
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n <= 6) return "<=6mo";
  if (n <= 12) return "7-12mo";
  if (n <= 24) return "13-24mo";
  return "24mo+";
}

/** What the spend figure was read off — the basis, not the amount. */
export function normalizeSpendBasis(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/per[\s_-]?night|nightly|\bnight\b|room rate/.test(s)) return "per night";
  if (/ticket|admission|entry fee|pass\b/.test(s)) return "per ticket";
  if (/flight|airfare|\bfare\b|airline/.test(s)) return "airfare";
  if (/per[\s_-]?(person|head|pax)|\bpp\b|each/.test(s)) return "per person";
  if (/total|package|whole trip|per[\s_-]?trip/.test(s)) return "per trip";
  if (/meal|dining|restaurant|food/.test(s)) return "food & drink";
  return "other";
}

/** Free-text "what they'll pay extra for" → six spend-lever buckets. Order
 *  matters: a phrase naming both a cabin and an experience is a cabin upgrade. */
export function normalizePremium(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (
    /business class|first class|cabin|seat|legroom|lounge|priority board|extra baggage|airfare|flight upgrade/.test(s)
  )
    return "flights & cabin";
  if (/room|suite|hotel|resort|villa|stay|accommodation|\bview\b.*room|balcon|upgrade.*room/.test(s))
    return "stays & rooms";
  if (/transfer|private car|chauffeur|driver|taxi|rental|limo|transport/.test(s)) return "transport & transfers";
  if (/dining|restaurant|food|meal|drink|\bbar\b|cuisine|tasting/.test(s)) return "food & drink";
  if (/spa|wellness|gear|equipment|photograph|camera|kit\b/.test(s)) return "gear & wellness";
  if (
    /experien|attraction|tour|ticket|admission|observation|iconic|view|activity|excursion|access|skip.?the.?line/.test(
      s,
    )
  )
    return "experiences & access";
  return "other";
}

/** Free-text influencing channel → the canonical channel it names. The raw
 *  values are the same handful of platforms in a dozen casings ("tripadvisor",
 *  "TripAdvisor", "TripAdvisor ratings"), so they fold rather than rank. */
export function normalizeChannel(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/tripadvisor|trip advisor/.test(s)) return "TripAdvisor";
  if (/instagram|\big\b/.test(s)) return "Instagram";
  if (/tiktok|tik tok/.test(s)) return "TikTok";
  if (/youtube/.test(s)) return "YouTube";
  if (/reddit/.test(s)) return "Reddit";
  if (/twitter|\bx\b/.test(s)) return "Twitter / X";
  if (/facebook|\bfb\b/.test(s)) return "Facebook";
  if (/blog|substack|newsletter/.test(s)) return "Blogs";
  if (/forum|community|group/.test(s)) return "Forums & communities";
  if (/friend|family|peer|word[\s_-]?of[\s_-]?mouth|recommend/.test(s)) return "Word of mouth";
  if (/google|search|seo/.test(s)) return "Search";
  if (/booking|expedia|agoda|airbnb|ota\b|platform/.test(s)) return "OTAs";
  if (/press|news|magazine|guide ?book|lonely planet|media/.test(s)) return "Press & guides";
  if (/prior visit|own experience|personal/.test(s)) return "Own experience";
  return "Other";
}

/** Free-text decision role → the four roles the sheet actually distinguishes.
 *  Order matters: "sole decision-maker" and "joint decision-maker" both contain
 *  "decision maker", so the qualifier is tested before the generic. */
export function normalizeDecisionRole(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/joint|co[\s_-]?decid|shared|together|spouse|partner/.test(s)) return "joint";
  if (/sole|only|alone/.test(s)) return "sole";
  if (/influenc|advis|particip|input|consult/.test(s)) return "influencer";
  if (/primary|main|lead|decision[\s_-]?maker|decider|planner|organis|organiz/.test(s)) return "primary";
  return null;
}

/** Split a joined multi-value trait ("English; Japanese", "a, b/c") into a list. */
export function splitMulti(v: string | null): string[] {
  if (!v) return [];
  return v
    .split(/[;,/]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/* ── The published dimension table ─────────────────────────────────────────
   One row per trait the product publishes a distribution for. This is the
   single definition: the Explorer's behaviour wall builds its cards from it,
   and the server's explorer rollup counts from it, so a dimension can never be
   on screen with buckets the assistant folds differently.

   `multi` marks a list-valued trait — one profile lands in several buckets
   (it names several channels, several barriers), so those percentages are over
   the profiles carrying ANY value and do NOT sum to 100.

   An empty `order` is an OPEN vocabulary (brands, keywords): the buckets are
   whatever the sample actually says, ranked by frequency and capped at `topN`.
*/

export type TraitLayer = "demo" | "beh";

export interface SegmentationDimension {
  id: string;
  title: string;
  layer: TraitLayer;
  /** Key inside the explorer payload's `demo` / `beh` trait map. */
  key: string;
  normalize: (v: string | null) => string | null;
  /** List-valued: split the raw value and count every fragment. */
  multi?: boolean;
  /** Fixed bucket set, in display order. Empty = open vocabulary. */
  order: string[];
  labels: Record<string, string>;
  /** Rows to keep when `order` is empty. */
  topN?: number;
  /** Rendered on the Explorer's "How they travel" wall. */
  wall?: boolean;
}

export const SEGMENTATION_DIMENSIONS: SegmentationDimension[] = [
  // -- Demographics (Explorer filters + archetype deep-dive) --
  {
    id: "age",
    title: "Age",
    layer: "demo",
    key: "age",
    normalize: normalizeAge,
    order: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"],
    labels: {},
  },
  { id: "gender", title: "Gender", layer: "demo", key: "gender", normalize: (v) => v, order: [], labels: {}, topN: 6 },
  {
    id: "income",
    title: "Income Proxy",
    layer: "demo",
    key: "income",
    normalize: normalizeIncome,
    order: ["low", "mid", "upper-mid", "high"],
    labels: {},
  },
  {
    id: "household",
    title: "Household",
    layer: "demo",
    key: "household",
    normalize: normalizeHousehold,
    order: [],
    labels: {},
    topN: 8,
  },
  {
    id: "decision",
    title: "Decision Role",
    layer: "demo",
    key: "decision",
    normalize: normalizeDecisionRole,
    order: ["primary", "joint", "influencer", "sole"],
    labels: {},
  },
  {
    id: "country",
    title: "Country of Residence",
    layer: "demo",
    key: "country",
    normalize: (v) => v,
    order: [],
    labels: {},
    topN: 12,
  },
  {
    id: "nationality",
    title: "Nationality",
    layer: "demo",
    key: "nationality",
    normalize: (v) => v,
    order: [],
    labels: {},
    topN: 12,
  },
  {
    id: "profession",
    title: "Profession",
    layer: "demo",
    key: "profession",
    normalize: canonicalName,
    order: [],
    labels: {},
    topN: 10,
  },
  {
    id: "languages",
    title: "Languages Spoken",
    layer: "demo",
    key: "languages",
    normalize: canonicalName,
    multi: true,
    order: [],
    labels: {},
    topN: 10,
  },

  // -- Behaviour. Transcribed from the PIE_DEFS this table replaced, NOT
  // re-authored: the bucket sets are a closed vocabulary that must match what
  // each normaliser actually emits, and five hand-written guesses (duration,
  // transport, booking, lead_time, research) silently rolled up EMPTY against
  // live data because no normaliser output was in the invented `order`.
  // ORDER IS LOAD-BEARING: the web derives PIE_DEFS from this slice by id.
  {
    id: "freq",
    title: "Trip Frequency",
    layer: "beh",
    key: "frequency",
    normalize: normalizeFrequency,
    order: ["very_frequent", "frequent", "occasional"],
    labels: { very_frequent: "very freq.", frequent: "frequent", occasional: "occasional" },
    wall: true,
  },
  {
    id: "spend",
    title: "Spend per Trip",
    layer: "beh",
    key: "spend",
    normalize: normalizeSpend,
    order: ["<500", "500-2K", "2K-5K", "5K+"],
    labels: {},
    wall: true,
  },
  {
    id: "accom",
    title: "Accommodation",
    layer: "beh",
    key: "accom_tier",
    normalize: normalizeAccom,
    order: ["budget", "midscale_chain", "upscale_chain", "boutique", "luxury"],
    labels: {
      budget: "budget",
      midscale_chain: "midscale",
      upscale_chain: "upscale",
      boutique: "boutique",
      luxury: "luxury",
    },
    wall: true,
  },
  {
    id: "comp",
    title: "Companion",
    layer: "beh",
    key: "companion",
    normalize: normalizeCompanion,
    order: ["solo", "couple", "family_with_kids", "group", "work"],
    labels: { solo: "solo", couple: "couple", family_with_kids: "family", group: "group", work: "work" },
    wall: true,
  },
  {
    id: "scope",
    title: "Geographic Scope",
    layer: "beh",
    key: "scope",
    normalize: normalizeScope,
    order: ["domestic", "international"],
    labels: { domestic: "domestic", international: "international" },
    wall: true,
  },
  {
    id: "sensitivity",
    title: "Budget Sensitivity",
    layer: "beh",
    key: "sensitivity",
    normalize: normalizeSensitivity,
    order: [
      "very_price_sensitive",
      "price_aware",
      "mixed",
      "experience_over_price",
      "price_insensitive",
      "aspirational",
    ],
    labels: {
      very_price_sensitive: "very price-sensitive",
      price_aware: "price-aware",
      mixed: "mixed",
      experience_over_price: "experience over price",
      price_insensitive: "price-insensitive",
      aspirational: "aspirational",
    },
    wall: true,
  },
  {
    id: "duration",
    title: "Trip Duration",
    layer: "beh",
    key: "duration",
    normalize: normalizeDuration,
    order: ["short_break", "one_week", "two_weeks", "extended"],
    labels: { short_break: "short break", one_week: "~1 week", two_weeks: "~2 weeks", extended: "extended" },
    wall: true,
  },
  {
    id: "transport",
    title: "Transport Mode",
    layer: "beh",
    key: "transport",
    normalize: normalizeTransport,
    order: ["flight", "car", "train", "other"],
    labels: { flight: "flight", car: "car / road", train: "train", other: "other" },
    wall: true,
  },
  {
    id: "booking",
    title: "Booking Channel",
    layer: "beh",
    key: "booking",
    normalize: normalizeBooking,
    order: ["direct", "ota", "agent"],
    labels: { direct: "direct", ota: "OTA / app", agent: "travel agent" },
    wall: true,
  },
  {
    id: "lead_time",
    title: "Planning Lead Time",
    layer: "beh",
    key: "lead_time",
    normalize: normalizeLeadTime,
    order: ["last_minute", "weeks", "months", "year_plus"],
    labels: { last_minute: "last-minute", weeks: "weeks ahead", months: "months ahead", year_plus: "a year+" },
    wall: true,
  },
  {
    id: "season",
    title: "Travel Season",
    layer: "beh",
    key: "season",
    normalize: normalizeSeason,
    order: ["summer", "winter", "shoulder", "year_round"],
    labels: { summer: "summer", winter: "winter", shoulder: "shoulder", year_round: "year-round" },
    wall: true,
  },
  {
    id: "research",
    title: "Research Depth",
    layer: "beh",
    key: "research",
    normalize: normalizeResearch,
    order: ["low", "moderate", "high"],
    labels: { low: "low", moderate: "moderate", high: "high" },
  },
  {
    id: "trips",
    title: "Trips Observed",
    layer: "beh",
    key: "trips",
    normalize: normalizeTripsObserved,
    order: ["1", "2-3", "4-6", "7+"],
    labels: { "1": "1 trip", "2-3": "2–3", "4-6": "4–6", "7+": "7+" },
    wall: true,
  },
  {
    id: "trips_window",
    title: "Observation Window",
    layer: "beh",
    key: "trips_window",
    normalize: normalizeObsWindow,
    order: ["<=6mo", "7-12mo", "13-24mo", "24mo+"],
    labels: { "<=6mo": "≤ 6 months", "7-12mo": "7–12 months", "13-24mo": "13–24 months", "24mo+": "2 years+" },
  },
  {
    id: "spend_basis",
    title: "Spend Basis",
    layer: "beh",
    key: "spend_basis",
    normalize: normalizeSpendBasis,
    order: ["per trip", "per night", "per person", "per ticket", "airfare", "food & drink", "other"],
    labels: {},
  },
  {
    id: "premium",
    title: "Pays Premium For",
    layer: "beh",
    key: "premium",
    normalize: normalizePremium,
    multi: true,
    order: [
      "experiences & access",
      "stays & rooms",
      "flights & cabin",
      "food & drink",
      "transport & transfers",
      "gear & wellness",
      "other",
    ],
    labels: {},
    wall: true,
  },
  {
    id: "barriers",
    title: "Barriers to Travel",
    layer: "beh",
    key: "barriers",
    normalize: normalizeBarriers,
    multi: true,
    order: ["cost", "entry & logistics", "safety & security", "personal"],
    labels: {},
    wall: true,
  },
  {
    id: "triggers",
    title: "Trigger Moments",
    layer: "beh",
    key: "triggers",
    normalize: normalizeTriggers,
    multi: true,
    order: ["seasonal & social", "milestone & celebration", "deals & rewards", "life transition"],
    labels: {},
    wall: true,
  },
  {
    id: "channels",
    title: "Influencing Channels",
    layer: "beh",
    key: "channels",
    normalize: normalizeChannel,
    multi: true,
    order: [],
    labels: {},
    topN: 8,
    wall: true,
  },
  {
    id: "brands",
    title: "Brands That Resonate",
    layer: "beh",
    key: "brands",
    normalize: canonicalName,
    multi: true,
    order: [],
    labels: {},
    wall: true,
  },
  {
    id: "accom_brand",
    title: "Accommodation Brands",
    layer: "beh",
    key: "accom_brand",
    normalize: canonicalName,
    multi: true,
    order: [],
    labels: {},
    wall: true,
  },
  {
    id: "airline",
    title: "Airlines Mentioned",
    layer: "beh",
    key: "airline",
    normalize: canonicalName,
    multi: true,
    order: [],
    labels: {},
    wall: true,
  },
  {
    id: "loyalty",
    title: "Loyalty Programs",
    layer: "beh",
    key: "loyalty",
    normalize: canonicalName,
    multi: true,
    order: [],
    labels: {},
    wall: true,
  },
  {
    id: "cues",
    title: "Cues & Keywords",
    layer: "beh",
    key: "cues",
    normalize: normalizeKeyword,
    multi: true,
    order: [],
    labels: {},
    topN: 8,
    wall: true,
  },
];

export const DIMENSION_BY_ID = new Map(SEGMENTATION_DIMENSIONS.map((d) => [d.id, d]));

/** Counted buckets for one dimension, plus the base every share is over.
 *  `withValue` is the profiles carrying ANY value — the honest denominator, and
 *  for a `multi` dimension the reason the buckets can exceed it. */
export interface TraitDistribution {
  id: string;
  title: string;
  multi: boolean;
  withValue: number;
  buckets: Record<string, number>;
}

/** Roll one dimension up over pre-extracted raw values, one entry per profile
 *  (null / absent for a profile that has no value). Shared so the server's
 *  aggregate and any client counting the same payload agree bucket for bucket. */
export function countTrait(dim: SegmentationDimension, rawPerProfile: (string | null)[]): TraitDistribution {
  const buckets: Record<string, number> = {};
  let withValue = 0;
  for (const raw of rawPerProfile) {
    const placed = new Set<string>();
    // The two paths mirror the web's two accessors EXACTLY, and the difference
    // between them is deliberate: a list-valued trait goes through
    // `fragmentsOf` (split → meaningfulFrag → normalise), a single-valued one
    // through the plain getter (normalise the raw value, no fragment filter).
    // Running meaningfulFrag over a single value too would drop rows the wall
    // counts, and the two would then disagree about the same profile.
    for (const frag of dim.multi ? splitMulti(raw) : raw != null ? [raw] : []) {
      const input = dim.multi ? meaningfulFrag(frag) : frag;
      if (!input) continue;
      const v = dim.normalize(input);
      // A fixed bucket set is a closed vocabulary: a value outside it is a
      // normaliser miss, not a new bucket, and must not render as a one-off
      // slice next to the real ones. This is the same test `distWithIndex`
      // applies when it plots only the keys named in `order`.
      if (!v || (dim.order.length > 0 && !dim.order.includes(v))) continue;
      placed.add(v);
    }
    if (placed.size === 0) continue;
    withValue += 1;
    for (const v of placed) buckets[v] = (buckets[v] ?? 0) + 1;
  }
  return { id: dim.id, title: dim.title, multi: Boolean(dim.multi), withValue, buckets };
}
